import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { API, LOGO_DARK } from '../config';

const EMPTY = {
  id: null, title: '', slug: '', excerpt: '', cover_image: '',
  tags: '', seo_title: '', seo_description: '', body: '', status: 'draft',
};

const AdminBlog = () => {
  const navigate = useNavigate();
  const [isAdmin, setIsAdmin] = useState(null);
  const [posts, setPosts] = useState([]);
  const [form, setForm] = useState(null); // null = list view
  const [saving, setSaving] = useState(false);
  const [msg, setMsg] = useState('');

  const loadPosts = async () => {
    try {
      const res = await axios.get(`${API}/blog/admin/posts`);
      setPosts(res.data.posts || []);
    } catch (e) { /* ignore */ }
  };

  useEffect(() => {
    (async () => {
      try {
        const me = await axios.get(`${API}/blog/admin/me`);
        if (!me.data.is_admin) { setIsAdmin(false); return; }
        setIsAdmin(true);
        await loadPosts();
      } catch (e) {
        setIsAdmin(false);
      }
    })();
  }, []);

  const startNew = () => { setForm({ ...EMPTY }); setMsg(''); };
  const startEdit = (p) => {
    setForm({ ...p, tags: (p.tags || []).join(', ') });
    setMsg('');
  };

  const save = async () => {
    if (!form.title.trim()) { setMsg('Title is required.'); return; }
    setSaving(true);
    setMsg('');
    const payload = {
      ...form,
      tags: form.tags.split(',').map((t) => t.trim()).filter(Boolean),
    };
    try {
      if (form.id) {
        await axios.put(`${API}/blog/admin/posts/${form.id}`, payload);
      } else {
        await axios.post(`${API}/blog/admin/posts`, payload);
      }
      await loadPosts();
      setForm(null);
    } catch (e) {
      setMsg(e.response?.data?.detail || 'Could not save.');
    }
    setSaving(false);
  };

  const remove = async (id) => {
    if (!window.confirm('Delete this post permanently?')) return;
    try {
      await axios.delete(`${API}/blog/admin/posts/${id}`);
      await loadPosts();
    } catch (e) { /* ignore */ }
  };

  if (isAdmin === null) {
    return <div className="min-h-screen bg-[#FAFAFA] flex items-center justify-center"><div className="animate-spin rounded-full h-10 w-10 border-b-2 border-emerald-600" /></div>;
  }

  if (isAdmin === false) {
    return (
      <div className="min-h-screen bg-[#FAFAFA] flex flex-col items-center justify-center gap-4 text-center px-4">
        <h1 className="text-2xl font-bold text-neutral-900">Admin only</h1>
        <p className="text-neutral-500">This area is restricted to the site owner.</p>
        <button onClick={() => navigate('/dashboard')} className="text-emerald-600 font-semibold">← Back to dashboard</button>
      </div>
    );
  }

  const input = 'w-full border border-neutral-300 rounded-xl px-4 py-2.5 focus:outline-none focus:ring-2 focus:ring-emerald-400 text-sm';

  return (
    <div className="min-h-screen bg-[#FAFAFA]">
      <header className="bg-white border-b border-neutral-200">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 py-4 flex items-center justify-between">
          <div className="flex items-center gap-2">
            <img src={LOGO_DARK} alt="Daily Discipline" className="h-8 w-8" />
            <span className="font-bold text-neutral-900">Blog Admin</span>
          </div>
          <Link to="/blog" className="text-sm text-neutral-500 hover:text-emerald-600">View blog →</Link>
        </div>
      </header>

      <main className="max-w-4xl mx-auto px-4 sm:px-6 py-8" data-testid="admin-blog-page">
        {!form ? (
          <>
            <div className="flex items-center justify-between mb-6">
              <h1 className="text-2xl font-bold text-neutral-900">Your Posts</h1>
              <button data-testid="new-post-button" onClick={startNew} className="bg-emerald-500 hover:bg-emerald-600 text-white font-semibold rounded-full px-5 py-2.5 text-sm transition">
                + New Post
              </button>
            </div>
            {posts.length === 0 ? (
              <p className="text-neutral-400 bg-white border border-neutral-200 rounded-2xl p-8 text-center">No posts yet. Write your first one ✍️</p>
            ) : (
              <div className="flex flex-col gap-3">
                {posts.map((p) => (
                  <div key={p.id} data-testid={`admin-post-${p.slug}`} className="bg-white border border-neutral-200 rounded-2xl p-4 flex items-center justify-between gap-4">
                    <div className="min-w-0">
                      <div className="flex items-center gap-2">
                        <span className={`text-[10px] uppercase font-bold px-2 py-0.5 rounded-full ${p.status === 'published' ? 'bg-emerald-100 text-emerald-700' : 'bg-amber-100 text-amber-700'}`}>{p.status}</span>
                        <h3 className="font-semibold text-neutral-900 truncate">{p.title}</h3>
                      </div>
                      <p className="text-xs text-neutral-400 mt-1 truncate">/blog/{p.slug}</p>
                    </div>
                    <div className="flex items-center gap-2 shrink-0">
                      <button onClick={() => startEdit(p)} className="text-emerald-600 hover:text-emerald-800 text-sm font-semibold">Edit</button>
                      <button onClick={() => remove(p.id)} className="text-red-500 hover:text-red-700 text-sm font-semibold">Delete</button>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </>
        ) : (
          <div className="bg-white border border-neutral-200 rounded-2xl p-6 flex flex-col gap-4">
            <div className="flex items-center justify-between">
              <h1 className="text-xl font-bold text-neutral-900">{form.id ? 'Edit Post' : 'New Post'}</h1>
              <button onClick={() => setForm(null)} className="text-neutral-400 hover:text-neutral-700 text-sm">← Back</button>
            </div>

            <label className="text-sm font-medium text-neutral-700">Title
              <input data-testid="post-title" className={input + ' mt-1'} value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} placeholder="The headline readers will see" />
            </label>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <label className="text-sm font-medium text-neutral-700">Slug (URL) <span className="text-neutral-400 font-normal">— optional</span>
                <input className={input + ' mt-1'} value={form.slug} onChange={(e) => setForm({ ...form, slug: e.target.value })} placeholder="auto-generated from title" />
              </label>
              <label className="text-sm font-medium text-neutral-700">Tags <span className="text-neutral-400 font-normal">— comma separated</span>
                <input className={input + ' mt-1'} value={form.tags} onChange={(e) => setForm({ ...form, tags: e.target.value })} placeholder="Habits, Identity" />
              </label>
            </div>

            <label className="text-sm font-medium text-neutral-700">Cover image URL
              <input className={input + ' mt-1'} value={form.cover_image} onChange={(e) => setForm({ ...form, cover_image: e.target.value })} placeholder="https://…" />
            </label>

            <label className="text-sm font-medium text-neutral-700">Excerpt <span className="text-neutral-400 font-normal">— short summary for cards & SEO</span>
              <textarea className={input + ' mt-1'} rows={2} value={form.excerpt} onChange={(e) => setForm({ ...form, excerpt: e.target.value })} />
            </label>

            <label className="text-sm font-medium text-neutral-700">Body <span className="text-neutral-400 font-normal">— Markdown supported</span>
              <textarea data-testid="post-body" className={input + ' mt-1 font-mono text-xs'} rows={16} value={form.body} onChange={(e) => setForm({ ...form, body: e.target.value })} placeholder="## Heading&#10;&#10;Write your post in Markdown…" />
            </label>

            <details className="text-sm">
              <summary className="font-medium text-neutral-700 cursor-pointer">Advanced SEO (optional)</summary>
              <div className="mt-3 flex flex-col gap-3">
                <input className={input} value={form.seo_title} onChange={(e) => setForm({ ...form, seo_title: e.target.value })} placeholder="SEO title (defaults to post title)" />
                <input className={input} value={form.seo_description} onChange={(e) => setForm({ ...form, seo_description: e.target.value })} placeholder="SEO meta description (defaults to excerpt)" />
              </div>
            </details>

            {msg && <p className="text-red-500 text-sm">{msg}</p>}

            <div className="flex items-center gap-3 pt-2">
              <select data-testid="post-status" className="border border-neutral-300 rounded-xl px-4 py-2.5 text-sm" value={form.status} onChange={(e) => setForm({ ...form, status: e.target.value })}>
                <option value="draft">Draft</option>
                <option value="published">Published</option>
              </select>
              <button data-testid="save-post" onClick={save} disabled={saving} className="bg-emerald-500 hover:bg-emerald-600 text-white font-semibold rounded-full px-6 py-2.5 text-sm transition disabled:opacity-50">
                {saving ? 'Saving…' : 'Save'}
              </button>
            </div>
          </div>
        )}
      </main>
    </div>
  );
};

export default AdminBlog;
